import React, { useMemo } from 'react';
import {
  ActivePopulationFormattedData,
  ConsumptionByGoodsFormattedData,
  FormattedData,
} from '../../modules/dashboard/charts/types';
import { useTypedSelector } from '../useTypedSelector';
import { RootState } from '../../reducer';

interface SocioeconomicChartsData {
  typesOfResidenceFormattedData: FormattedData[];
  residenceConditionFormattedData: FormattedData[];
  dwellersByResidenceFormattedData: FormattedData[];
  genderDistributionFormattedData: FormattedData[];
  activePopulationFormattedData: ActivePopulationFormattedData[];
  consumptionByGoodsFormattedData: ConsumptionByGoodsFormattedData;
  ageDistribution: Record<string, string>;
  consumptionByIncome: Record<
    string,
    { average_consumption: string; households: string }
  >;
  householdsByIncome: Record<string, Record<string, string>>;
  populationGrowth: Record<string, Record<string, string>>;
}

export const useSocioeconomicChartsData = (): SocioeconomicChartsData => {
  const {
    chartsDataSocioeconomicDemographic,
    buildingModalData,
    tablesDataSocioeconomicDemographic,
  } =
    useTypedSelector((state: any) => state.socioeconomicDemographicState) || '';
  const { statusModal } = useTypedSelector(
    (state: RootState) => state.dashboardState
  );
  const isModal = statusModal === 'open building socioeconomic demographic';

  const chartsData = isModal
    ? buildingModalData.chartsData
    : chartsDataSocioeconomicDemographic;
  const tablesData = isModal
    ? buildingModalData.tablesData
    : tablesDataSocioeconomicDemographic;
  const typesOfResidenceFormattedData = useMemo((): FormattedData[] => {
    let formattedChartData = [];
    if (!chartsData?.typesOfResidence) {
      return [];
    } else {
      for (let key in chartsData?.typesOfResidence) {
        formattedChartData.push({
          name: chartsData?.typesOfResidence[key]?.label,
          formattedValue: chartsData?.typesOfResidence[key]?.formattedValue,
          value: chartsData?.typesOfResidence[key]?.count,
          percentage: chartsData?.typesOfResidence[key]?.percentage,
        });
      }
      return formattedChartData;
    }
  }, [chartsData?.typesOfResidence]);

  const residenceConditionFormattedData = useMemo((): FormattedData[] => {
    let formattedChartData = [];
    if (!chartsData?.residenceCondition) {
      return [];
    } else {
      for (let key in chartsData?.residenceCondition) {
        formattedChartData.push({
          name: chartsData?.residenceCondition[key]?.label,
          value: chartsData?.residenceCondition[key]?.value,
          formattedValue: chartsData?.residenceCondition[key]?.formattedValue,
          percentage: chartsData?.residenceCondition[key]?.percentage,
        });
      }
      return formattedChartData;
    }
  }, [chartsData?.residenceCondition]);

  const dwellersByResidenceFormattedData = useMemo((): FormattedData[] => {
    if (!chartsData?.dwellersByResidence) {
      return [];
    } else {
      const formattedChartData: FormattedData[] =
        chartsData?.dwellersByResidence.map((chartElem: any) => {
          return {
            name: chartElem?.label,
            value: chartElem?.residents,
            formattedValue: chartElem?.formattedValue,
            percentage: chartElem?.percentual,
          };
        });
      return formattedChartData;
    }
  }, [chartsData?.dwellersByResidence]);

  const genderDistributionFormattedData = useMemo((): FormattedData[] => {
    let formattedChartData = [];
    if (!chartsData?.genderDistribution) {
      return [];
    } else {
      for (let key in chartsData?.genderDistribution) {
        const label = key === 'men' ? 'Homens' : 'Mulheres';
        formattedChartData.push({
          name: label,
          value: chartsData?.genderDistribution[key]?.value,
          formattedValue: chartsData?.genderDistribution[key]?.formattedValue,
          percentage: chartsData?.genderDistribution[key]?.percentage,
        });
      }
      return formattedChartData;
    }
  }, [chartsData?.genderDistribution]);

  const activePopulationFormattedData =
    useMemo((): ActivePopulationFormattedData[] => {
      let formattedChartData: ActivePopulationFormattedData[] = [];
      if (!chartsData?.activePopulation) {
        return [];
      } else {
        formattedChartData.push({
          workers: chartsData?.activePopulation?.activePopulation?.total,
          inactivePopulation:
            chartsData?.activePopulation?.inactivePopulation?.total,
          labels: {
            workers: chartsData?.activePopulation?.activePopulation?.value,
            inactivePopulation:
              chartsData?.activePopulation?.inactivePopulation?.total,
            total: chartsData?.activePopulation?.total?.formattedValue,
          },
        });
        return formattedChartData;
      }
    }, [chartsData?.activePopulation]);

  const consumptionByGoodsFormattedData =
    useMemo((): ConsumptionByGoodsFormattedData => {
      if (!chartsData?.consumptionByGoods) {
        return {} as ConsumptionByGoodsFormattedData;
      } else {
        return {
          homeMaintenance:
            chartsData?.consumptionByGoods?.[
              'Constru\u00e7\u00e3o e bricolagem'
            ]?.[0],
          constructionMaterials:
            chartsData?.consumptionByGoods?.[
              'Constru\u00e7\u00e3o e bricolagem'
            ]?.[1],
          dwelling:
            chartsData?.consumptionByGoods?.['Habita\u00e7\u00e3o']?.[0],
          homeAppliances:
            chartsData?.consumptionByGoods?.['Habita\u00e7\u00e3o']?.[1],
          homeFurniture:
            chartsData?.consumptionByGoods?.['Habita\u00e7\u00e3o']?.[2],
        };
      }
    }, [chartsData?.consumptionByGoods]);

  return {
    typesOfResidenceFormattedData,
    residenceConditionFormattedData,
    dwellersByResidenceFormattedData,
    genderDistributionFormattedData,
    activePopulationFormattedData,
    consumptionByGoodsFormattedData,
    ...tablesData,
  };
};
